<template>
  <div
    class="product-card-carousel"
    :class="{ 'product-card-carousel--is-product-page': isProductPage }"
  >
    <div class="product-card-carousel__header">
      <h2
        class="product-card-carousel__title text-heading-m"
        v-text="title"
      />
    </div>

    <splide
      v-if="useCarousel"
      class="product-card-carousel__carousel"
      :has-track="false"
      :options="config"
      @splide:moved="handleSplideMoved"
    >
      <div class="product-card-carousel__arrows splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button
          class="splide__arrow splide__arrow--next"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>

      <splide-track>
        <splide-slide
          v-for="product of carouselData"
          :key="product.id"
        >
          <product-card
            hide-tagline
            :product="product"
          />
        </splide-slide>
      </splide-track>
    </splide>

    <ul
      v-else
      class="product-card-carousel__track"
    >
      <li
        v-for="product of carouselData"
        :key="product.id"
        class="product-card-carousel__product"
      >
        <product-card
          hide-tagline
          :product="product"
        />
      </li>
    </ul>
  </div>
</template>

<script>

/**
 * Vue: Product card carousel (product-card-carousel)
 * -----------------------------------------------------------------------------
 * Carousel of product cards, with differnt styling depending on page type.
 *
 * @param {Array} carouselData - Array of formatted products.
 * @param {String} [pageType] - Shopify request.page_type response.
 * @param {String} [title] - Section title.
 *
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { values as breakpoints } from '~/config/breakpoints'

import ProductCard from '~async/product-card/product-card'

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  name: 'ProductCardCarousel',

  components: {
    IconArrowLeft,
    IconArrowRight,
    ProductCard,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    carouselData: {
      type: Array,
      default: () => [],
      required: true,
    },
    pageType: {
      type: String,
      default: 'index',
    },
    title: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      config: {
        arrows: true,
        autoplay: false,
        breakpoints: {
          [breakpoints.l]: {
            padding: {
              right: '1rem',
            },
            perPage: 4,
          },
        },
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        gap: '1rem',
        mediaQuery: 'min',
        padding: { left: '1rem', right: '35%' },
        pagination: false,
        perMove: 1,
        perPage: 1,
        start: 0,
        type: 'slide',
      },
      currentIndex: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return if the snippet is being used on the product page.
     * @returns Boolean
     */
    isProductPage() {
      return this.pageType === 'product'
    },

    /**
     * Determine whether to render splide carousel based on page type and
     * resolution.
     * @returns {Boolean}
     */
    useCarousel() {
      if (!this.isProductPage) {
        return this.isMobile
      }

      return true
    },
  },

  methods: {

    /**
     * Handle events when Splide has moved.
     * @param {Integer} newIndex - New splide index.
     */
    handleSplideMoved(_, newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-card-carousel';
</style>
